import * as amplitude from "@amplitude/analytics-browser";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CircularProgress,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RiUploadLine } from "react-icons/ri";
import { useLocation } from "react-router-dom";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { AdminPage } from "src/components/Layout/AdminPage";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { AdminHeading } from "src/components/Text/AdminHeading";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  CapacityValidationError,
  CapacityValidationWarning,
  importCapacitiesByEnrollmentPeriod,
  ImportCapacitiesByEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useFileInput } from "src/hooks/useFilePicker";
import { useOrganization } from "src/hooks/useOrganization";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import * as breadcrumb from "src/services/breadcrumb";
import { parseCsvAsync } from "src/services/dataTransfer";
import * as Url from "src/services/url";
import { ImportSummary } from "./ImportSummary";

type ImportState = { state: "idle" } | { state: "importing" } | ImportedState;

export const useImportCapacities = ({
  enrollmentPeriodId,
}: {
  enrollmentPeriodId: string;
}): {
  importState: ImportState;
  importButton: React.ReactNode;
  importPage: React.ReactNode;
} => {
  const [state, setState] = React.useState<ImportState>({ state: "idle" });
  const organization = useOrganization();
  const location = useLocation();
  React.useEffect(() => {
    if (organization.hasData()) {
      if (
        location.pathname ===
        Url.OrgAdmin.CapacitiesConfig.index(organization.data)
      ) {
        setState({ state: "idle" });
      }
    }
  }, [location, organization]);

  const ep = useEnrollmentPeriod();
  const avelaToast = useAvelaToast();

  const queryClient = useQueryClient();

  const doImport = React.useCallback(
    async (rows: unknown[]) => {
      setState({ state: "importing" });
      try {
        const response = await importCapacitiesByEnrollmentPeriod({
          path: { enrollmentPeriodId },
          body: rows as any,
        });

        if (response.data) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
            }),
            queryClient.invalidateQueries({
              queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
            }),
            queryClient.invalidateQueries({
              queryKey: [FSQ.useGetCapacitiesHistoryKey],
              exact: false,
            }),
          ]);
          setState({
            state: "imported",
            result: response.data.data,
          });
        }
      } catch (error) {
        console.error(error);
        avelaToast.genericError();
      }
    },
    [avelaToast, enrollmentPeriodId, queryClient]
  );

  const { openFilePicker, inputElement } = useFileInput({
    accept: ".csv",
    onFilesSelected: async ([file]) => {
      if (!file) return;
      try {
        const rows = await parseCsvAsync(file);
        await doImport(rows);
      } catch (error) {
        console.error(error);
        avelaToast.genericError();
      }
    },
  });

  const userPermissions = useUserPermissions();
  const isAllowedToImportCapacity = userPermissions.hasOne(
    "school.grade:update"
  );
  const importButton = userPermissions.isLoading ? (
    <Skeleton width="6rem" height="2.5rem" />
  ) : isAllowedToImportCapacity ? (
    <>
      {inputElement}
      <Button
        leftIcon={<Icon as={RiUploadLine} />}
        size="md"
        variant="outline"
        onClick={() => {
          amplitude.track("Clicked import all");
          openFilePicker();
        }}
      >
        Import
      </Button>
    </>
  ) : null;

  const importPage = (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.capacities.getBreadcrumbsForImport(organization)}
        />
      }
      heading={<AdminHeading title="Import capacities" />}
    >
      {state.state === "imported" ? (
        <Flex direction="column" flexGrow="1" gap="4">
          <ImportResult state={state} />
          <Spacer />
          <AdminFormButtons>
            <Button type="button" onClick={() => setState({ state: "idle" })}>
              Done
            </Button>
          </AdminFormButtons>
        </Flex>
      ) : null}
      {state.state === "importing" ? (
        <Flex
          direction="column"
          width="100%"
          height="100%"
          flexGrow="1"
          alignItems="center"
          justifyContent="center"
          gap="2"
        >
          <CircularProgress isIndeterminate color="primary.500" />
          <Text fontSize="xl">Updating capacities</Text>
          <Text>
            Import in progress
            {ep.selectedEnrollmentPeriod
              ? ` for ${ep.selectedEnrollmentPeriod.name}. `
              : ". "}
            This may take a few moments.
          </Text>
        </Flex>
      ) : null}
    </AdminPage>
  );

  return {
    importState: state,
    importButton,
    importPage,
  };
};

type ImportedState = {
  state: "imported";
  result: ImportCapacitiesByEnrollmentPeriodResponse["data"];
};
type ImportResultProps = {
  state: ImportedState;
};
const ImportResult: React.FC<ImportResultProps> = ({ state }) => {
  const errors = [
    ...state.result.validationResult.errors,
    ...state.result.validationResult.warnings,
  ];
  const totalErrors = errors.length;

  return (
    <>
      <Flex direction="column" gap="4">
        {totalErrors > 0 ? (
          <Alert status="warning" rounded="md">
            <AlertIcon />
            <AlertDescription display="flex" gap="2">
              <Text>{totalErrors} errors importing capacities - </Text>
              <ButtonWithErrorDialog errors={errors} />
            </AlertDescription>
          </Alert>
        ) : null}

        <ImportSummary
          config={state.result.config}
          importResult={state.result.importResult}
        />
      </Flex>
    </>
  );
};

const ButtonWithErrorDialog = ({
  errors,
}: {
  errors: (CapacityValidationError | CapacityValidationWarning)[];
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <Button color="orange.900" variant="link" onClick={onOpen}>
        See details
      </Button>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200">
            Import errors
          </ModalHeader>
          <ModalBody as={Flex} gap="2" direction="column">
            <Box>
              Import failed on some rows. Review the imported file, and retry
              the upload for the following rows:
            </Box>
            <Flex direction="column" gap="1">
              {errors.map((error) => (
                <ValidationError
                  key={
                    error.kind === "row"
                      ? error.rowNumber +
                        (error.columnName ?? "") +
                        error.level +
                        error.message
                      : error.level + error.message
                  }
                  error={error}
                />
              ))}
            </Flex>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.200">
            <Button onClick={onClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const ValidationError = ({
  error,
}: {
  error: CapacityValidationError | CapacityValidationWarning;
}) => {
  if (error.kind === "row") {
    return (
      <Flex gap="2">
        <Box>{error.rowNumber}</Box> -<Box>{error.message}</Box>
        <Spacer />
        <Level level={error.level} />
      </Flex>
    );
  } else {
    return (
      <Flex gap="2">
        <Box>{error.message}</Box>
        <Spacer />
        <Level level={error.level} />
      </Flex>
    );
  }
};

const Level: React.FC<{ level: "error" | "warning" }> = ({ level }) => {
  if (level === "error")
    return (
      <Tag size="sm" colorScheme="red" alignSelf="flex-start" marginTop="2px">
        Error
      </Tag>
    );
  if (level === "warning")
    return (
      <Tag
        size="sm"
        colorScheme="orange"
        alignSelf="flex-start"
        marginTop="2px"
      >
        Warning
      </Tag>
    );
};
